/**
 * Web Audio API Emergency Siren Synthesizer
 * Generates an alternating 800Hz - 1200Hz high-decibel emergency siren tone
 * that plays for 10-20 seconds with stop/mute controls.
 */
class EmergencySiren {
  constructor() {
    this.audioCtx = null;
    this.oscillator = null;
    this.gainNode = null;
    this.lfo = null;
    this.isPlaying = false;
    this.timeoutId = null;
  }

  init() {
    if (!this.audioCtx) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioCtx = new AudioContext();
      }
    }
  }

  /**
   * Play emergency siren for durationSeconds (default 15s)
   */
  play(durationSeconds = 15) {
    try {
      this.init();
      if (!this.audioCtx) return;

      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
      }

      this.stop(); // Stop any currently playing audio

      const now = this.audioCtx.currentTime;

      // Main Oscillator
      this.oscillator = this.audioCtx.createOscillator();
      this.oscillator.type = 'sawtooth';
      this.oscillator.frequency.setValueAtTime(850, now);

      // LFO for frequency modulation (siren pitch oscillation between 800Hz and 1250Hz)
      this.lfo = this.audioCtx.createOscillator();
      this.lfo.frequency.setValueAtTime(2.5, now); // 2.5 oscillations per second

      const lfoGain = this.audioCtx.createGain();
      lfoGain.gain.setValueAtTime(350, now); // frequency deviation +/- 350Hz

      this.lfo.connect(lfoGain);
      lfoGain.connect(this.oscillator.frequency);

      // Volume Gain Node
      this.gainNode = this.audioCtx.createGain();
      this.gainNode.gain.setValueAtTime(0.3, now);
      // Gentle ramp in
      this.gainNode.gain.exponentialRampToValueAtTime(0.5, now + 0.3);

      this.oscillator.connect(this.gainNode);
      this.gainNode.connect(this.audioCtx.destination);

      this.oscillator.start(now);
      this.lfo.start(now);
      this.isPlaying = true;

      // Auto stop after durationSeconds
      this.timeoutId = setTimeout(() => {
        this.stop();
      }, durationSeconds * 1000);
    } catch (e) {
      console.warn('Emergency siren audio could not play:', e);
    }
  }

  /**
   * Immediately silence siren
   */
  stop() {
    try {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      if (this.oscillator) {
        this.oscillator.stop();
        this.oscillator.disconnect();
        this.oscillator = null;
      }
      if (this.lfo) {
        this.lfo.stop();
        this.lfo.disconnect();
        this.lfo = null;
      }
      this.isPlaying = false;
    } catch (e) {
      // ignore
    }
  }
}

export const sirenPlayer = new EmergencySiren();
